import { defineStore } from 'pinia'
import type GeoJSON from 'geojson'
import type { MapLayer, MapStyleType } from '~/composables/map'
import type { MapStore } from '~/stores/types'
import type { AssetType, ColorConfig } from '~/types/asset'
import type { EffectiveAlertLevelColor, MapAssetObject } from '~/types/map-assets.types'
import { TOTAL_LOADING_STEPS } from '~/composables/map/config/progressiveLoadingConfig'

export type ZoneVisibilityMode = 'off' | 'border' | 'full'

const DEFAULT_ZOOM = 12
const DEFAULT_INTERACTIVE_LAYERS = ['assets-composite-layer']

export const useMapStore = defineStore(
  'mapStore',
  (): MapStore => {
    // State
    const isLoadingIds = ref<boolean>(false)
    const pendingAssetsRequests = ref<number>(0)

    const currentLoadingStep = ref<number>(0)
    const totalLoadingSteps = ref<number>(TOTAL_LOADING_STEPS)
    const stepProgress = ref<Record<number, number>>({})
    const isProgressiveLoading = ref<boolean>(false)
    const progressiveLoadCache = ref<MapAssetObject[] | null>(null)

    let progressiveCacheTimeout: ReturnType<typeof setTimeout> | null = null

    const currentCenter = ref<Coords | undefined>()
    const currentZoom = ref<number>(DEFAULT_ZOOM)
    const currentBounds = ref<Bounds | undefined>()
    const currentStyle = ref<MapStyleType>('streets')
    const lastStyle = ref<MapStyleType>('satellite')
    const layers = ref<MapLayer[]>([])
    const polygons = ref<GeoJSON.Feature[]>([])
    const thumbnails = ref<Record<MapStyleType, string>>({
      streets: '',
      terrain: '',
      satellite: ''
    })
    const useDynamicThumbnails = ref<boolean>(false)
    const isInitialized = ref<boolean>(false)
    const isStyleInitialized = ref<boolean>(false)
    const showAssetNames = ref<boolean>(false)
    const enableAssetClustering = ref<boolean>(true)
    const groupClustersByColor = ref<boolean>(true)
    const excludeGatewaysFromClusters = ref<boolean>(true)
    const clusterEffectRadius = ref<number>(200)
    const colorConfigs = ref<ColorConfig[]>([])
    const assetTypes = ref<AssetType[]>([])
    const alertLevelColors = ref<EffectiveAlertLevelColor[]>([])
    const assetInteractiveLayerIds = ref<string[]>([...DEFAULT_INTERACTIVE_LAYERS])
    const zoneVisibilityMode = ref<ZoneVisibilityMode>('full')
    const allowAssetOverlap = ref<boolean>(true)
    const areStyleThumbnailsEnlarged = ref<boolean>(false)

    // Computed
    const isLoadingAssets = computed<boolean>(() => pendingAssetsRequests.value > 0)

    /**
     * Calculate overall loading progress (0-100)
     * Each step contributes equally to the total progress
     */
    const overallLoadingProgress = computed<number>(() => {
      if (!isProgressiveLoading.value || currentLoadingStep.value === 0) {
        return 0
      }

      const completedSteps = currentLoadingStep.value - 1
      const currentStepProgress = stepProgress.value[currentLoadingStep.value] || 0
      const progressPerStep = 100 / totalLoadingSteps.value

      return Math.min(100, completedSteps * progressPerStep + (currentStepProgress * progressPerStep) / 100)
    })

    // Actions
    const addPendingAssetsRequest = (): void => {
      pendingAssetsRequests.value += 1
    }
    const removePendingAssetsRequest = (): void => {
      if (pendingAssetsRequests.value > 0) {
        pendingAssetsRequests.value -= 1
      }
    }
    const setCurrentCenter = (center: Coords): void => {
      currentCenter.value = center
    }
    const setCurrentZoom = (zoom: number): void => {
      currentZoom.value = zoom
    }
    const setCurrentBounds = (bounds: Bounds): void => {
      currentBounds.value = bounds
    }
    const addPolygon = (polygon: GeoJSON.Feature): void => {
      polygons.value = [...polygons.value, polygon]
    }
    const removePolygon = (polygonId: string | number): void => {
      polygons.value = polygons.value.filter((item: GeoJSON.Feature) => item.id !== polygonId)
    }
    const clearPolygons = (): void => {
      polygons.value = []
    }
    const registerLayer = (layer: MapLayer): void => {
      if (layers.value.find((l: MapLayer) => l.id === layer.id)) {
        return
      }
      layers.value.push(layer)
    }
    const unregisterLayer = (layerId: string): void => {
      layers.value.filter((l: MapLayer) => l.id !== layerId)
    }
    const setMapStyle = (style: MapStyleType): void => {
      lastStyle.value = currentStyle.value
      currentStyle.value = style
    }
    const setStyleInitialized = (v: boolean): void => {
      isStyleInitialized.value = v
    }
    const setShowAssetNames = (show: boolean): void => {
      showAssetNames.value = show
    }
    const setAssetClustering = (enabled: boolean): void => {
      enableAssetClustering.value = enabled
    }
    const setGroupClustersByColor = (enabled: boolean): void => {
      groupClustersByColor.value = enabled
    }
    const setExcludeGatewaysFromClusters = (enabled: boolean): void => {
      excludeGatewaysFromClusters.value = enabled
    }
    const setClusterEffectRadius = (radius: number): void => {
      clusterEffectRadius.value = radius
    }
    const setAssetInteractiveLayers = (layerIds: string[]): void => {
      assetInteractiveLayerIds.value = [...layerIds]
    }
    const setUseDynamicThumbnails = (enabled: boolean): void => {
      useDynamicThumbnails.value = enabled
    }
    const getStaticThumbnail = (style: MapStyleType): string => {
      switch (style) {
        case 'satellite':
          return '/thumbnails/map-satellite.png'
        case 'terrain':
          return '/thumbnails/map-terrain.png'
        default:
          return '/thumbnails/map-streets.png'
      }
    }
    const setMapColorConfig = (configs: ColorConfig[]): void => {
      colorConfigs.value = configs
    }
    const setAssetTypesLoaded = (types: AssetType[]): void => {
      assetTypes.value = types
    }
    const setAlertLevelColorsLoaded = (colors: EffectiveAlertLevelColor[]): void => {
      alertLevelColors.value = colors
    }
    const setZoneVisibilityMode = (mode: ZoneVisibilityMode): void => {
      zoneVisibilityMode.value = mode
    }
    const setAllowAssetOverlap = (enabled: boolean): void => {
      allowAssetOverlap.value = enabled
    }
    const setStyleThumbnailsEnlarged = (enlarged: boolean): void => {
      areStyleThumbnailsEnlarged.value = enlarged
    }

    // Progressive loading actions
    const setLoadingStep = (step: number, progress: number): void => {
      currentLoadingStep.value = step
      stepProgress.value[step] = progress
    }
    const startProgressiveLoading = (): void => {
      isProgressiveLoading.value = true
      currentLoadingStep.value = 1
      stepProgress.value = {}
    }
    const completeProgressiveLoading = (): void => {
      isProgressiveLoading.value = false
      currentLoadingStep.value = 0
      stepProgress.value = {}
    }
    const resetLoadingProgress = (): void => {
      currentLoadingStep.value = 0
      stepProgress.value = {}
      isProgressiveLoading.value = false
    }

    const setProgressiveLoadCache = (assets: MapAssetObject[]): void => {
      progressiveLoadCache.value = assets

      if (progressiveCacheTimeout) {
        clearTimeout(progressiveCacheTimeout)
      }
      progressiveCacheTimeout = setTimeout(() => {
        clearProgressiveLoadCache()
      }, 30000)
    }

    const clearProgressiveLoadCache = (): void => {
      if (progressiveCacheTimeout) {
        clearTimeout(progressiveCacheTimeout)
        progressiveCacheTimeout = null
      }
      progressiveLoadCache.value = null
    }

    const $reset = (): void => {
      isLoadingIds.value = false
      pendingAssetsRequests.value = 0

      // Reset progressive loading
      currentLoadingStep.value = 0
      totalLoadingSteps.value = TOTAL_LOADING_STEPS
      stepProgress.value = {}
      isProgressiveLoading.value = false
      clearProgressiveLoadCache()

      currentCenter.value = undefined
      currentZoom.value = DEFAULT_ZOOM
      currentBounds.value = undefined
      currentStyle.value = 'streets'
      lastStyle.value = 'satellite'
      layers.value = []
      polygons.value = []
      thumbnails.value = {
        streets: '',
        terrain: '',
        satellite: ''
      }
      useDynamicThumbnails.value = false
      isInitialized.value = false
      isStyleInitialized.value = false
      showAssetNames.value = false
      enableAssetClustering.value = true
      groupClustersByColor.value = true
      excludeGatewaysFromClusters.value = true
      clusterEffectRadius.value = 50
      colorConfigs.value = []
      assetTypes.value = []
      alertLevelColors.value = []
      assetInteractiveLayerIds.value = [...DEFAULT_INTERACTIVE_LAYERS]
      zoneVisibilityMode.value = 'full'
      allowAssetOverlap.value = true
      areStyleThumbnailsEnlarged.value = false
    }

    return {
      pendingAssetsRequests,
      isLoadingIds,
      currentLoadingStep,
      totalLoadingSteps,
      stepProgress,
      isProgressiveLoading,
      progressiveLoadCache,
      currentCenter,
      currentZoom,
      currentBounds,
      currentStyle,
      lastStyle,
      layers,
      polygons,
      thumbnails,
      useDynamicThumbnails,
      isInitialized,
      isStyleInitialized,
      showAssetNames,
      enableAssetClustering,
      groupClustersByColor,
      excludeGatewaysFromClusters,
      clusterEffectRadius,
      colorConfigs,
      assetTypes,
      alertLevelColors,
      assetInteractiveLayerIds,
      zoneVisibilityMode,
      allowAssetOverlap,
      areStyleThumbnailsEnlarged,

      isLoadingAssets,
      overallLoadingProgress,

      // Actions
      addPendingAssetsRequest,
      removePendingAssetsRequest,
      setCurrentCenter,
      setCurrentZoom,
      setCurrentBounds,
      addPolygon,
      removePolygon,
      clearPolygons,
      registerLayer,
      unregisterLayer,
      setMapStyle,
      setStyleInitialized,
      setShowAssetNames,
      setAssetClustering,
      setGroupClustersByColor,
      setExcludeGatewaysFromClusters,
      setClusterEffectRadius,
      setMapColorConfig,
      setAssetTypesLoaded,
      setAlertLevelColorsLoaded,
      setAssetInteractiveLayers,
      setUseDynamicThumbnails,
      getStaticThumbnail,
      setZoneVisibilityMode,
      setAllowAssetOverlap,
      setStyleThumbnailsEnlarged,
      setLoadingStep,
      startProgressiveLoading,
      completeProgressiveLoading,
      resetLoadingProgress,
      setProgressiveLoadCache,
      clearProgressiveLoadCache,
      $reset
    }
  },
  {
    persist: {
      storage: piniaPluginPersistedstate.localStorage(),
      omit: [
        'isLoadingAssets',
        'isLoadingIds',
        'pendingAssetsRequests',
        'currentLoadingStep',
        'totalLoadingSteps',
        'stepProgress',
        'isProgressiveLoading',
        'progressiveLoadCache',
        'overallLoadingProgress',
        'polygons',
        'layers',
        'isInitialized',
        'isStyleInitialized',
        'assetInteractiveLayerIds',
        'areStyleThumbnailsEnlarged'
      ]
    }
  }
)
